/**
 * *"Were you away?"* — asked on the path, before the first lesson back
 * ([D-154](../../../docs/DECISIONS.md#d-154)).
 *
 * The retroactive grace ([D-133](../../../docs/DECISIONS.md#d-133)) only
 * works if the holiday is declared before a lesson settles the streak, and a
 * setting in a menu is not where a child looks on the way to a lesson. So the
 * path asks, once, and only when the server says a "yes" would work: there is
 * a streak, days were missed, and the rules would accept the holiday.
 *
 * "No" closes it and says nothing more. A streak that then breaks is never
 * announced ([Gamification](../../../docs/GAMIFICATION.md#when-a-streak-does-break)),
 * and this is not the place to start.
 */

import { useEffect, useState } from 'react';
import { type Client, today } from './api.js';
import Sneakers from './Sneakers.js';

interface Offer {
  readonly startDay: string;
  readonly days: number;
  readonly streak: number;
}

/**
 * Offers this device has had "no" for, kept while the app is open.
 *
 * Not stored: a child who says no is on their way into a lesson, and that
 * lesson settles the streak, after which the server stops offering anyway.
 */
const declined = new Set<string>();
const keyOf = (offer: Offer): string => `${offer.startDay}+${String(offer.days)}`;

export default function WereYouAway({ client }: { readonly client: Client }) {
  const [offer, setOffer] = useState<Offer | null>(null);
  const [said, setSaid] = useState<'kept' | 'refused' | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    // Through `Promise.resolve`: a server older than this app has no
    // `awayOffer`, or no `vacation` router at all (D-066). Either is silence.
    Promise.resolve()
      .then(() => client.vacation.status.query({ today: today() }))
      .then((status) => {
        const next = (status as { awayOffer?: Offer | null }).awayOffer ?? null;
        if (live && next !== null && !declined.has(keyOf(next))) setOffer(next);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [client]);

  if (offer === null) return null;

  if (said !== null) {
    return (
      <div className="card" role="status" style={{ marginTop: 14 }}>
        {said === 'kept'
          ? `Your ${String(offer.streak)}-day streak is safe. Welcome back!`
          : 'Welcome back! Let’s play.'}
      </div>
    );
  }

  const yes = () => {
    if (busy) return;
    setBusy(true);
    Promise.resolve()
      .then(() =>
        client.vacation.start.mutate({
          today: today(),
          startDay: offer.startDay,
          days: offer.days,
        }),
      )
      .then((result) => {
        setSaid(result.ok ? 'kept' : 'refused');
      })
      .catch(() => {
        setSaid('refused');
      });
  };

  const no = () => {
    declined.add(keyOf(offer));
    setOffer(null);
  };

  return (
    <section className="card" aria-label="Were you away?" style={{ marginTop: 14 }}>
      <Sneakers
        text="You’re back! I missed you."
        audioRef={null}
        size={44}
        speak={false}
      />
      <p style={{ marginTop: 10, fontWeight: 700 }}>
        {offer.days === 1
          ? 'You missed a day. Were you away — on a trip, or ill?'
          : `You missed ${String(offer.days)} days. Were you away — on a trip, or ill?`}
      </p>
      <p className="tiny" style={{ marginTop: 4 }}>
        If you were, I’ll keep your <span aria-hidden="true">🔥</span>{' '}
        {`${String(offer.streak)}-day streak safe.`}
      </p>
      <button
        className="btn"
        type="button"
        style={{ marginTop: 10 }}
        disabled={busy}
        onClick={yes}
      >
        Yes, I was away
      </button>
      <button className="btn ghost" type="button" style={{ marginTop: 8 }} onClick={no}>
        No
      </button>
    </section>
  );
}

/** For tests: forget every "no" this module remembers. */
export const forgetDeclined = (): void => {
  declined.clear();
};
